import { CheckCircle, ChevronLeft, ChevronRight, Clock, Lock, Shield, Star, Users, Zap } from "lucide-react";
import { useEffect,useRef, useState } from "react";
import { FaEnvelope,FaFacebook, FaInstagram, FaWhatsapp } from "react-icons/fa";

import { TPlanResponse } from "@/pages/clients/use-client-plans.hooks";
import { formatPrice } from "@/utils/monetary-formatter";

interface IInsuranceLandingPageProps {
  title: string;
  description: string;
  primaryButtonText: string;
  imageBannerPrimary?: string;
  primaryLogo?: string;
  secondaryLogo?: string;
  whatsappNumber?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  contactEmail?: string;
  primaryColor?: string;
  selectedPlans?: TPlanResponse[];
}

export default function InsuranceLandingPage({
  title,
  description,
  primaryButtonText,
  imageBannerPrimary,
  primaryLogo,
  secondaryLogo,
  whatsappNumber,
  instagramUrl,
  facebookUrl,
  contactEmail,
  primaryColor,
  selectedPlans,
}: IInsuranceLandingPageProps) {
  const accent = primaryColor || "#3B6EF5";

  // ── Carousel de planos ──
  const PLAN_GAP = 12;
  const planTrackRef = useRef<HTMLDivElement>(null);
  const [planTrackWidth, setPlanTrackWidth] = useState(0);
  const [planIndex, setPlanIndex] = useState(0);
  const VISIBLE_PLANS = 3;

  const plansList =
    selectedPlans && selectedPlans.length > 0
      ? selectedPlans
      : ([
          { id: "1", name: "Plano Essencial", plan_price: 6990, is_active: false, description: "Proteção básica" },
          { id: "2", name: "Plano Completo", plan_price: 10990, is_active: true, description: "Mais popular" },
          { id: "3", name: "Plano Premium", plan_price: 14990, is_active: false, description: "Cobertura máxima" },
        ] as any[]);

  const planMaxIndex = Math.max(0, plansList.length - VISIBLE_PLANS);
  const isPlanCarousel = plansList.length > VISIBLE_PLANS;
  const planCardWidth =
    planTrackWidth > 0
      ? (planTrackWidth - PLAN_GAP * (VISIBLE_PLANS - 1)) / VISIBLE_PLANS
      : 0;
  const planTranslateX = planIndex * (planCardWidth + PLAN_GAP);

  useEffect(() => {
    const update = () => {
      if (planTrackRef.current) setPlanTrackWidth(planTrackRef.current.offsetWidth);
    };
    update();
    const ro = new ResizeObserver(update);
    if (planTrackRef.current) ro.observe(planTrackRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setPlanIndex((i) => Math.min(i, planMaxIndex));
  }, [planMaxIndex]);

  const steps = [
    { icon: "📋", label: "Preencha seus dados" },
    { icon: "🔍", label: "Análise do perfil" },
    { icon: "✅", label: "Escolha seu plano" },
    { icon: "🚀", label: "Ative sua proteção" },
  ];

  const benefits = [
    { icon: <Shield size={20} />, title: "Proteção Completa", desc: "Cobertura abrangente para todas as situações" },
    { icon: <Zap size={20} />, title: "100% Digital", desc: "Contratação rápida, sem burocracia" },
    { icon: <Users size={20} />, title: "Atendimento 24h", desc: "Suporte especializado quando precisar" },
  ];

  const testimonials = [
    { name: "Ana Silva", city: "São Paulo, SP", text: "Contratei em minutos! Processo simples e proteção completa.", avatar: "https://i.pravatar.cc/40?img=47" },
    { name: "Carlos Lima", city: "Belo Horizonte, MG", text: "Atendimento impecável. Recomendo muito!", avatar: "https://i.pravatar.cc/40?img=12" },
    { name: "Fernanda Costa", city: "Curitiba, PR", text: "Melhor custo-benefício que encontrei. Aprovado!", avatar: "https://i.pravatar.cc/40?img=32" },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden text-sm">

      {/* ── HEADER ── */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            {primaryLogo ? (
              <img src={primaryLogo} alt="Logo" className="max-h-8 max-w-[7rem] object-contain" />
            ) : (
              <div className="h-8 w-10 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: accent }}>LP</div>
            )}
            {secondaryLogo && (
              <img src={secondaryLogo} alt="Logo secundário" className="max-h-8 max-w-[7rem] object-contain" />
            )}
          </div>
          <button
            className="text-xs font-semibold text-white px-4 py-2 rounded-full"
            style={{ backgroundColor: accent }}
          >
            {primaryButtonText || "Simular Agora"}
          </button>
        </div>
      </header>

      {/* ── HERO ── */}
      <section
        className="py-12 px-6 overflow-hidden relative"
        style={{ background: `linear-gradient(160deg, ${accent}0D 0%, ${accent}06 60%, #ffffff 100%)` }}
      >
        {/* blob */}
        <div className="absolute top-[-10%] right-[-5%] w-64 h-64 rounded-full opacity-10 blur-3xl pointer-events-none" style={{ backgroundColor: accent }} />

        <div className="grid lg:grid-cols-2 gap-8 items-center max-w-5xl mx-auto">
          {/* Left */}
          <div className="space-y-4">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white border border-gray-200"
              style={{ color: accent }}
            >
              ✓ Proteção Completa
            </span>
            <h1 className="text-3xl font-bold text-gray-900 leading-tight">
              <span style={{ color: accent }}>{title || "Proteja sua Família"}</span>
            </h1>
            <div className="text-gray-500 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: description || "Planos completos com as melhores coberturas do mercado." }} />
            <button
              className="text-sm font-semibold text-white px-6 py-2.5 rounded-full shadow-md"
              style={{ backgroundColor: accent }}
            >
              {primaryButtonText || "Ver Planos"}
            </button>

            {/* trust + rating */}
            <div className="flex items-center gap-3 pt-1">
              <div className="flex -space-x-1.5">
                {["47", "12", "44", "57"].map((img) => (
                  <img key={img} src={`https://i.pravatar.cc/32?img=${img}`} className="w-7 h-7 rounded-full border-2 border-white object-cover" alt="" />
                ))}
              </div>
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={12} fill="#F59E0B" color="#F59E0B" />
                ))}
              </div>
              <span className="text-xs text-gray-500 font-medium">4.9 · 12.000+ clientes</span>
            </div>

            {/* trust badges */}
            <div className="flex flex-wrap gap-3 text-xs text-gray-500">
              {[
                { icon: <Lock size={11} />, label: "100% Seguro" },
                { icon: <CheckCircle size={11} />, label: "Sem burocracia" },
                { icon: <Clock size={11} />, label: "Aprovação em minutos" },
              ].map((t) => (
                <div key={t.label} className="flex items-center gap-1">
                  <span style={{ color: accent }}>{t.icon}</span>
                  {t.label}
                </div>
              ))}
            </div>
          </div>

          {/* Right: image + floating cards */}
          <div className="relative flex justify-center">
            {imageBannerPrimary ? (
              <img src={imageBannerPrimary} alt="Banner" className="max-h-72 rounded-2xl shadow-xl object-cover" />
            ) : (
              <div className="w-64 h-48 rounded-2xl bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-400 text-xs">Imagem do banner</div>
            )}
            {/* floating card */}
            <div className="absolute -bottom-3 -left-4 bg-white rounded-xl shadow-lg px-3 py-2 flex items-center gap-2 text-xs">
              <span className="text-lg">✅</span>
              <div>
                <p className="font-bold text-gray-800">Aprovado!</p>
                <p className="text-gray-400">em 5 minutos</p>
              </div>
            </div>
            <div className="absolute -top-3 -right-4 bg-white rounded-xl shadow-lg px-3 py-2 flex items-center gap-2 text-xs">
              <span className="text-lg">🛡️</span>
              <div>
                <p className="font-bold text-gray-800">Família Protegida</p>
                <p className="text-gray-400">Cobertura nacional</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PLANOS ── */}
      <section className="py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl font-bold text-gray-900 text-center mb-8">Sua tranquilidade começa com o plano certo</h2>

          {/* Carousel wrapper */}
          <div className="relative">
            {/* Arrows */}
            {isPlanCarousel && (
              <>
                <button
                  onClick={() => setPlanIndex((i) => Math.max(0, i - 1))}
                  disabled={planIndex === 0}
                  className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white border flex items-center justify-center shadow transition-all disabled:opacity-30"
                  style={{ borderColor: accent, color: accent }}
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => setPlanIndex((i) => Math.min(planMaxIndex, i + 1))}
                  disabled={planIndex >= planMaxIndex}
                  className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white border flex items-center justify-center shadow transition-all disabled:opacity-30"
                  style={{ borderColor: accent, color: accent }}
                >
                  <ChevronRight size={16} />
                </button>
              </>
            )}

            {/* Track */}
            <div ref={planTrackRef} className="overflow-hidden pt-4">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ gap: `${PLAN_GAP}px`, transform: `translateX(-${planTranslateX}px)` }}
              >
                {plansList.map((plan: any) => (
                  <div
                    key={plan.id}
                    className="flex-shrink-0 relative border rounded-2xl p-5 flex flex-col gap-3 bg-white"
                    style={{
                      width: planCardWidth > 0 ? `${planCardWidth}px` : `calc((100% - ${PLAN_GAP * (VISIBLE_PLANS - 1)}px) / ${VISIBLE_PLANS})`,
                      borderColor: plan.is_active ? accent : "#E5E7EB",
                      boxShadow: plan.is_active ? "0 4px 20px rgba(0,0,0,0.08)" : undefined,
                    }}
                  >
                    {plan.is_active && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-white text-[10px] font-bold px-3 py-0.5 rounded-full" style={{ backgroundColor: accent }}>
                        Mais Popular
                      </div>
                    )}
                    <p className="font-bold text-gray-900 text-sm">{plan.name}</p>
                    <p className="text-xs text-gray-400">{plan.description}</p>
                    <p className="text-2xl font-bold" style={{ color: accent }}>
                      {formatPrice(plan.plan_price)}
                      <span className="text-xs text-gray-400 font-normal">/mês</span>
                    </p>
                    <button
                      className="w-full text-xs font-semibold text-white py-2 rounded-lg mt-auto"
                      style={{ backgroundColor: plan.is_active ? accent : "#1F2937" }}
                    >
                      Escolher Plano
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Dots */}
            {isPlanCarousel && (
              <div className="flex justify-center items-center gap-1.5 mt-5">
                {Array.from({ length: planMaxIndex + 1 }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPlanIndex(i)}
                    className="rounded-full transition-all duration-300"
                    style={{
                      width: i === planIndex ? "20px" : "6px",
                      height: "6px",
                      backgroundColor: i === planIndex ? accent : "#D1D5DB",
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── COMO FUNCIONA ── */}
      <section className="py-12 px-6" style={{ background: "linear-gradient(135deg, #080D24 0%, #0D1A42 100%)" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <span className="text-xs font-semibold tracking-widest uppercase text-blue-400">Passo a passo</span>
            <h2 className="text-xl font-bold text-white mt-2">
              Como <span style={{ color: accent }}>funciona</span>
            </h2>
          </div>

          {/* Progress line + steps */}
          <div className="relative">
            <div className="absolute top-5 left-[12.5%] right-[12.5%] h-0.5 hidden md:block" style={{ backgroundColor: accent }} />
            <div className="grid md:grid-cols-4 gap-4">
              {steps.map((step, i) => (
                <div
                  key={i}
                  className="relative rounded-xl p-4 flex flex-col items-center gap-2 text-center border-transparent shadow-lg transition-all"
                  style={{ backgroundColor: `${accent}20`, borderColor: accent }}
                >
                  <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ backgroundColor: accent }} />
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mb-1 text-white" style={{ backgroundColor: accent }}>
                    {i + 1}
                  </div>
                  <span className="text-lg">{step.icon}</span>
                  <p className="text-xs font-medium text-white">{step.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── BENEFÍCIOS ── */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: accent }}>Por que nos escolher</span>
            <h2 className="text-xl font-bold text-gray-900 mt-2">Vantagens que fazem diferença</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {benefits.map((b) => (
              <div key={b.title} className="border border-gray-100 rounded-2xl p-5 flex flex-col gap-3 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accent}15`, color: accent }}>
                  {b.icon}
                </div>
                <p className="font-bold text-gray-900 text-sm">{b.title}</p>
                <p className="text-xs text-gray-500">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DEPOIMENTOS ── */}
      <section className="py-12 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: accent }}>Avaliações</span>
            <h2 className="text-xl font-bold text-gray-900 mt-2">O que nossos clientes dizem</h2>
            <div className="flex items-center justify-center gap-1.5 mt-2 text-xs text-gray-400">
              {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={12} fill="#F59E0B" color="#F59E0B" />)}
              <span className="font-semibold text-gray-600 ml-1">4.9</span>
              <span>· 2.000+ avaliações</span>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-white border border-gray-100 rounded-2xl p-4 flex flex-col gap-3">
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={11} fill="#F59E0B" color="#F59E0B" />)}
                </div>
                <p className="text-xs text-gray-600 flex-1">"{t.text}"</p>
                <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                  <img src={t.avatar} alt={t.name} className="w-8 h-8 rounded-full object-cover" />
                  <div>
                    <p className="text-xs font-semibold text-gray-900">{t.name}</p>
                    <p className="text-[10px] text-gray-400">{t.city}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-12 px-6 text-center" style={{ background: "linear-gradient(135deg, #080D24 0%, #0D1A42 100%)" }}>
        <div className="max-w-xl mx-auto space-y-4">
          <h2 className="text-2xl font-bold text-white">
            Pronto para proteger sua <span style={{ color: accent }}>família hoje?</span>
          </h2>
          <p className="text-sm text-white/60">Simule gratuitamente e descubra o plano ideal.</p>
          <button
            className="text-sm font-semibold text-white px-8 py-3 rounded-full shadow-lg"
            style={{ backgroundColor: accent }}
          >
            {primaryButtonText || "Quero Simular Agora"} →
          </button>
          <div className="flex items-center justify-center gap-6 text-xs text-white/40 pt-2">
            <span className="flex items-center gap-1"><Lock size={11} /> Seguro</span>
            <span className="flex items-center gap-1"><CheckCircle size={11} /> Sem burocracia</span>
            <span className="flex items-center gap-1"><Zap size={11} /> Rápido</span>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-gray-950 text-white py-10 px-6">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8">
          <div className="space-y-3">
            {primaryLogo ? (
              <img src={primaryLogo} alt="Logo" className="max-h-8 max-w-[7rem] object-contain brightness-200" />
            ) : (
              <div className="h-8 w-10 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: accent }}>LP</div>
            )}
            <p className="text-xs text-gray-400">Proteção e segurança para toda sua família.</p>
            <div className="flex gap-3">
              {whatsappNumber && <FaWhatsapp size={16} className="text-gray-500 hover:text-green-400 cursor-pointer transition-colors" />}
              {instagramUrl && <FaInstagram size={16} className="text-gray-500 hover:text-pink-400 cursor-pointer transition-colors" />}
              {facebookUrl && <FaFacebook size={16} className="text-gray-500 hover:text-blue-400 cursor-pointer transition-colors" />}
              {contactEmail && <FaEnvelope size={16} className="text-gray-500 hover:text-sky-400 cursor-pointer transition-colors" />}
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold tracking-widest uppercase text-gray-500 mb-3">Empresa</p>
            {["Sobre Nós", "Política de Privacidade", "Termos de Uso"].map((item) => (
              <p key={item} className="text-xs text-gray-400">{item}</p>
            ))}
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold tracking-widest uppercase text-gray-500 mb-3">Contato</p>
            {whatsappNumber && (
              <p className="text-xs text-gray-400 flex items-center gap-1.5"><FaWhatsapp size={12} className="text-green-500" />{whatsappNumber}</p>
            )}
            {contactEmail && (
              <p className="text-xs text-gray-400 flex items-center gap-1.5"><FaEnvelope size={12} className="text-sky-400" />{contactEmail}</p>
            )}
          </div>
        </div>
        <div className="max-w-5xl mx-auto mt-8 pt-6 border-t border-gray-800 text-center text-xs text-gray-500">
          © {new Date().getFullYear()} Soulhub. Todos os direitos reservados.
        </div>
      </footer>
    </div>
  );
}
